import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Typography, Progress, Radio, Space, message, Steps, Tabs, List, Tag, Empty, Input, Popconfirm } from 'antd';
import { HeartOutlined, ArrowLeftOutlined, ArrowRightOutlined, CheckCircleOutlined, HistoryOutlined, CloudOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import { MOODS, MOOD_MAP } from '../config/moods';

// 配置axios基础URL，指向后端服务
axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

const Emotions = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [historySessions, setHistorySessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetails, setSessionDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('guide');

  // 树洞：一句话心情
  const [treeList, setTreeList] = useState([]);
  const [treeContent, setTreeContent] = useState('');
  const [treeMood, setTreeMood] = useState('平和');
  const [treeLoading, setTreeLoading] = useState(false);
  const [treeSaving, setTreeSaving] = useState(false);

  const getToken = () => localStorage.getItem('token') || (localStorage.getItem('auth-storage') ? JSON.parse(localStorage.getItem('auth-storage')).state.token : '');
  const authCfg = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

  const fetchTreeHole = useCallback(() => {
    setTreeLoading(true);
    axios.get('/emotions/diary', authCfg())
      .then((r) => { if (r.data?.success) setTreeList(r.data.diary || []); })
      .catch(() => {})
      .finally(() => setTreeLoading(false));
  }, []);

  const addTreeHole = async () => {
    const content = treeContent.trim();
    if (!content) { message.warning('写一句话吧～'); return; }
    setTreeSaving(true);
    try {
      const r = await axios.post('/emotions/diary', { content, mood: treeMood }, authCfg());
      if (r.data?.success) {
        setTreeContent('');
        setTreeList((prev) => [r.data.entry, ...prev]);
        message.success('已记录');
      } else {
        message.error(r.data?.message || '记录失败');
      }
    } catch (e) {
      message.error('记录失败');
    } finally {
      setTreeSaving(false);
    }
  };

  const deleteTreeHole = async (id) => {
    try {
      const r = await axios.delete(`/emotions/diary/${id}`, authCfg());
      if (r.data?.success) setTreeList((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      message.error('删除失败');
    }
  };

  // 情绪管理五步法
  const steps = [
    {
      title: '觉察',
      subtitle: 'Awareness',
      description: '停下来，问自己现在的情绪和身体反应'
    },
    {
      title: '命名',
      subtitle: 'Labeling', 
      description: '给情绪取名字，减弱情绪强度'
    },
    {
      title: '调节',
      subtitle: 'Regulation',
      description: '选择合适的方法让情绪缓和'
    },
    {
      title: '表达',
      subtitle: 'Expression',
      description: '找信任的人说出来或写在日记里'
    },
    {
      title: '转化',
      subtitle: 'Reframe',
      description: '把情绪当信号，转为正面动力'
    }
  ];

  // 每个步骤的问题和选项
  const questions = {
    0: [ // 觉察步骤 - 第1步单选，第2步多选
      {
        question: '你现在感觉是什么情绪？',
        options: ['愤怒', '焦虑', '悲伤', '恐惧', '沮丧', '其他'],
        type: 'emotion',
        multiple: false
      },
      {
        question: '你的身体有什么反应？',
        options: ['心跳加快', '手心出汗', '呼吸急促', '肌肉紧张', '胃部不适', '其他'],
        type: 'physical',
        multiple: true
      }
    ],
    1: [ // 命名步骤 - 文本输入
      {
        question: '请给这个情绪取一个名字：',
        options: [],
        type: 'label',
        multiple: false,
        inputType: 'text'
      }
    ],
    2: [ // 调节步骤 - 多选
      {
        question: '你希望用什么方法来调节情绪？',
        options: ['深呼吸练习', '暂时离开情境', '听音乐', '运动', '冥想', '其他'],
        type: 'regulation',
        multiple: true
      }
    ],
    3: [ // 表达步骤 - 多选
      {
        question: '你倾向于如何表达这个情绪？',
        options: ['和信任的朋友聊天', '写在日记里', '画画或创作', '寻求专业帮助', '暂时保留'],
        type: 'expression',
        multiple: true
      }
    ],
    4: [ // 转化步骤 - 多选
      {
        question: '这个情绪提醒你什么需求没被满足？',
        options: ['需要更多尊重', '需要安全感', '需要理解和支持', '需要成就感', '需要休息'],
        type: 'reframe',
        multiple: true
      }
    ]
  };

  const handleAnswer = (answer) => {
    const question = getCurrentQuestion();
    const currentKey = `${currentStep}-${currentQuestion}`;
    
    console.log('handleAnswer 被调用:', {
      answer,
      currentStep,
      currentQuestion,
      currentKey,
      isLastStep: currentStep === steps.length - 1,
      isLastQuestion: currentQuestion === questions[currentStep].length - 1
    });
    
    if (question.inputType === 'text') {
      // 文本输入逻辑：直接保存输入的文本
      setAnswers({
        ...answers,
        [currentKey]: answer
      });
    } else if (question.multiple) {
      // 多选逻辑
      const currentAnswers = answers[currentKey] || [];
      const newAnswers = currentAnswers.includes(answer)
        ? currentAnswers.filter(item => item !== answer)
        : [...currentAnswers, answer];
      
      setAnswers({
        ...answers,
        [currentKey]: newAnswers
      });
    } else {
      // 单选逻辑
      setAnswers({
        ...answers,
        [currentKey]: answer
      });
    }
  };

  const nextQuestion = () => {
    // 如果是最后一步，不允许自动跳转
    if (currentStep === steps.length - 1 && currentQuestion === questions[currentStep].length - 1) {
      console.log('阻止最后一步自动跳转');
      return;
    }
    
    if (currentQuestion < questions[currentStep].length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
        setCurrentQuestion(0);
      }
    }
  };



  const canProceed = () => {
    const question = getCurrentQuestion();
    const currentKey = `${currentStep}-${currentQuestion}`;
    const currentAnswer = answers[currentKey];
    const isLastStep = currentStep === steps.length - 1;
    const isLastQuestion = currentQuestion === questions[currentStep].length - 1;
    
    console.log('canProceed 检查:', {
      currentStep,
      currentQuestion,
      currentKey,
      currentAnswer,
      isLastStep,
      isLastQuestion
    });
    
    // 如果是最后一步，永远返回 false，阻止自动跳转
    if (isLastStep && isLastQuestion) {
      console.log('最后一步，阻止自动跳转');
      return false;
    }
    
    if (question.inputType === 'text') {
      // 文本输入：必须有内容
      return currentAnswer && currentAnswer.trim() !== '';
    } else if (question.multiple) {
      // 多选：至少选择一个选项
      return currentAnswer && currentAnswer.length > 0;
    } else {
      // 单选：必须选择一个选项
      return currentAnswer && currentAnswer !== '';
    }
  };

  // 获取历史会话
  const fetchHistorySessions = async () => {
    setLoading(true);
    try {
      console.log('开始获取历史会话...');
      const token = localStorage.getItem('token') || (localStorage.getItem('auth-storage') ? JSON.parse(localStorage.getItem('auth-storage')).state.token : '');
      console.log('Token:', token ? 'Token存在' : 'Token不存在');
      console.log('Token长度:', token ? token.length : 0);
      
      const response = await axios.get('/emotions/sessions', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('历史会话API响应状态:', response.status);
      console.log('历史会话API响应状态文本:', response.statusText || 'OK');
      
      if (response.status !== 200) {
        console.error('HTTP错误:', response.status, response.statusText);
        console.error('错误响应内容:', response.data);
        message.error(`获取历史会话失败: ${response.status} ${response.statusText}`);
        return;
      }
      
      const data = response.data;
      console.log('历史会话API响应数据:', data);
      
      if (data.success) {
        console.log('设置历史会话数据:', data.sessions);
        setHistorySessions(data.sessions || []);
      } else {
        console.error('获取历史会话API返回失败:', data);
        message.error(data.message || '获取历史会话失败');
      }
    } catch (error) {
      console.error('获取历史会话失败:', error);
      console.error('错误详情:', error.message);
      console.error('错误堆栈:', error.stack);
      message.error(`获取历史会话失败: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 获取会话详情
  const fetchSessionDetails = async (sessionId) => {
    try {
      const token = localStorage.getItem('token') || (localStorage.getItem('auth-storage') ? JSON.parse(localStorage.getItem('auth-storage')).state.token : '');
      const response = await axios.get(`/emotions/session/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = response.data;
      if (data.success && data.session) {
        // 服务端返回单条会话（answers 为以「步骤-问题」为键的对象），
        // 这里转换成详情列表所需的数组结构
        const s = data.session;
        let answersObj = s.answers;
        if (typeof answersObj === 'string') {
          try { answersObj = JSON.parse(answersObj); } catch (_) { answersObj = {}; }
        }
        answersObj = answersObj || {};
        const details = Object.keys(answersObj)
          .sort((a, b) => {
            const [as, aq] = a.split('-').map(Number);
            const [bs, bq] = b.split('-').map(Number);
            return as - bs || aq - bq;
          })
          .map((key) => {
            const [stepIdx] = key.split('-').map(Number);
            const val = answersObj[key];
            const isObj = val && typeof val === 'object' && !Array.isArray(val);
            const answerVal = isObj ? val.answer : val;
            return {
              id: key,
              step_name: steps[stepIdx]?.title || `步骤${stepIdx + 1}`,
              step_order: stepIdx + 1,
              question: isObj ? (val.question || '') : '',
              answer: Array.isArray(answerVal) ? answerVal.join('、') : (answerVal ?? ''),
            };
          });
        setSessionDetails(details);
        setSelectedSession(sessionId);
      }
    } catch (error) {
      console.error('获取会话详情失败:', error);
      message.error('获取会话详情失败');
    }
  };

  // 保存完整会话到数据库
  const saveSession = async (answers) => {
    try {
      // 获取JWT token
      const token = localStorage.getItem('token') || (localStorage.getItem('auth-storage') ? JSON.parse(localStorage.getItem('auth-storage')).state.token : '');
      
      console.log('=== 保存会话调试信息 ===');
      console.log('Token存在:', !!token);
      console.log('Token长度:', token ? token.length : 0);
      console.log('Token前20字符:', token ? token.substring(0, 20) : 'N/A');
      
      if (!token) {
        console.error('未找到认证token');
        message.error('请先登录');
        return null;
      }
      
      // 验证答案完整性
      const expectedKeys = ['0-0', '0-1', '1-0', '2-0', '3-0', '4-0'];
      const missingKeys = expectedKeys.filter(key => !answers[key]);
      
      if (missingKeys.length > 0) {
        console.error('缺少答案:', missingKeys);
        message.error('请完成所有步骤后再保存');
        return null;
      }
      
      // 生成情绪摘要
      const emotionAnswer = answers['0-0'] || '';
      const emotionSummary = `主要情绪：${emotionAnswer}`;
      
      // 准备答案和问题文本
      const answersWithQuestions = {};
      Object.entries(answers).forEach(([key, value]) => {
        const [step, question] = key.split('-');
        const questionData = questions[parseInt(step)][parseInt(question)];
        console.log(`处理答案 ${key}:`, { value, question: questionData.question });
        answersWithQuestions[key] = {
          answer: value,
          question: questionData.question
        };
      });
      
      // 调试信息：打印所有答案
      console.log('保存会话，所有答案:', answers);
      console.log('答案数量:', Object.keys(answers).length);
      console.log('发送的数据:', {
        // user_id 现在从JWT token中获取，不需要在请求中传递
        answers: answersWithQuestions,
        emotion_summary: emotionSummary
      });
      
      const response = await axios.post('/emotions/save-session', {
        // user_id 现在从JWT token中获取，不需要在请求中传递
        answers: answersWithQuestions,
        emotion_summary: emotionSummary
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('API响应状态:', response.status);
      const data = response.data;
      console.log('API响应数据:', data);
      
      if (data.success) {
        console.log('会话保存成功，session_id:', data.session_id);
        return data.session_id;
      } else {
        console.error('API返回失败:', data);
        message.error('保存失败：' + (data.message || '未知错误'));
        return null;
      }
    } catch (error) {
      console.error('保存会话失败:', error);
      message.error('保存失败：网络错误');
      return null;
    }
  };

  // 组件加载时获取历史会话
  useEffect(() => {
    fetchHistorySessions();
  }, []);

  // 调试函数
  const debugToken = () => {
    const authData = localStorage.getItem('auth-storage');
    console.log('=== Token调试信息 ===');
    console.log('auth-storage原始数据:', authData);
    if (authData) {
      try {
        const parsed = JSON.parse(authData);
        console.log('解析后的auth数据:', parsed);
        console.log('Token:', parsed.state?.token);
        console.log('Token长度:', parsed.state?.token?.length || 0);
      } catch (e) {
        console.error('解析auth数据失败:', e);
      }
    } else {
      console.log('没有找到auth-storage数据，用户可能未登录');
    }
    console.log('==================');
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setCurrentQuestion(questions[currentStep - 1].length - 1);
    }
  };

  const getCurrentQuestion = () => {
    return questions[currentStep][currentQuestion];
  };

  const getProgress = () => {
    const totalQuestions = Object.values(questions).flat().length;
    const answeredQuestions = Object.keys(answers).length;
    return Math.round((answeredQuestions / totalQuestions) * 100);
  };

  const renderStepContent = () => {
    const question = getCurrentQuestion();
    const currentKey = `${currentStep}-${currentQuestion}`;
    const currentAnswer = answers[currentKey];

    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <div style={{ marginBottom: '30px' }}>
          <Title level={3} style={{ color: '#FF6B6B', marginBottom: '10px' }}>
            {steps[currentStep].title} ({steps[currentStep].subtitle})
          </Title>
          <Text style={{ color: '#666', fontSize: '16px' }}>
            {steps[currentStep].description}
          </Text>
        </div>

        <Card style={{ 
          maxWidth: '600px', 
          margin: '0 auto', 
          borderRadius: '16px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <Title level={4} style={{ marginBottom: '20px', color: '#333' }}>
            {question.question}
            {question.multiple && (
              <Text style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                (可多选)
              </Text>
            )}
            {question.inputType === 'text' && (
              <Text style={{ fontSize: '14px', color: '#666', marginLeft: '8px' }}>
                (请自由输入)
              </Text>
            )}
          </Title>
          
          {question.inputType === 'text' ? (
            // 文本输入
            <div style={{ width: '100%' }}>
              <input
                type="text"
                value={currentAnswer || ''}
                onChange={(e) => handleAnswer(e.target.value)}
                placeholder="请输入你的情绪名称..."
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  fontSize: '14px',
                  border: '1px solid #d9d9d9',
                  borderRadius: '6px',
                  outline: 'none',
                  transition: 'all 0.3s ease'
                }}
              />
            </div>
          ) : question.multiple ? (
            // 多选 - 使用Checkbox
            <div style={{ width: '100%' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                {question.options.map((option, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 12px',
                      marginBottom: '8px',
                      borderRadius: '6px',
                      border: '1px solid #d9d9d9',
                      backgroundColor: currentAnswer && currentAnswer.includes(option) ? '#f0f8ff' : '#ffffff',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleAnswer(option)}
                  >
                    <input
                      type="checkbox"
                      checked={currentAnswer && currentAnswer.includes(option)}
                      onChange={() => handleAnswer(option)}
                      style={{ marginRight: '12px' }}
                    />
                    <span style={{ fontSize: '14px', color: '#333' }}>{option}</span>
                  </div>
                ))}
              </Space>
            </div>
          ) : (
            // 单选 - 使用Radio
            <Radio.Group 
              value={currentAnswer} 
              onChange={(e) => {
                console.log('Radio onChange 被触发:', e.target.value);
                const isLastStep = currentStep === steps.length - 1;
                const isLastQuestion = currentQuestion === questions[currentStep].length - 1;
                
                if (isLastStep && isLastQuestion) {
                  console.log('最后一步，只保存答案，不触发其他逻辑');
                  handleAnswer(e.target.value);
                } else {
                  handleAnswer(e.target.value);
                }
              }}
              style={{ width: '100%' }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                {question.options.map((option, index) => (
                  <Radio
                    key={index}
                    value={option}
                    style={{
                      display: 'block',
                      height: 'auto',
                      lineHeight: '1.5',
                      padding: '8px 12px',
                      marginBottom: '8px',
                      borderRadius: '6px',
                      border: '1px solid #d9d9d9',
                      backgroundColor: currentAnswer === option ? '#f0f8ff' : '#ffffff',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <span style={{ fontSize: '14px', color: '#333' }}>{option}</span>
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          )}

          <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'space-between' }}>
            <Button 
              icon={<ArrowLeftOutlined />}
              onClick={prevQuestion}
              disabled={currentStep === 0 && currentQuestion === 0}
              size="large"
            >
              上一步
            </Button>
            
            {(() => {
              const isLastStep = currentStep === steps.length - 1;
              const isLastQuestion = currentQuestion === questions[currentStep].length - 1;
              const shouldShowSubmit = isLastStep && isLastQuestion;
              
              console.log('按钮渲染检查:', {
                currentStep,
                currentQuestion,
                isLastStep,
                isLastQuestion,
                shouldShowSubmit,
                canProceed: canProceed()
              });
              
              return shouldShowSubmit ? (
                // 最后一步显示提交按钮
              <Button 
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => {
                  // 确保当前问题的答案也被保存
                  const currentKey = `${currentStep}-${currentQuestion}`;
                  const currentAnswer = answers[currentKey];
                  console.log('准备提交，当前答案:', answers);
                  console.log('答案数量:', Object.keys(answers).length);
                  console.log('当前步骤:', currentStep, '当前问题:', currentQuestion);
                  console.log('当前答案键:', currentKey, '当前答案值:', currentAnswer);
                  
                  // 验证所有答案是否完整
                  const allAnswers = { ...answers };
                  if (!allAnswers[currentKey]) {
                    console.error('当前问题答案缺失:', currentKey);
                    message.error('请完成当前问题后再提交');
                    return;
                  }
                  
                  console.log('准备保存的完整答案:', allAnswers);
                  console.log('答案键列表:', Object.keys(allAnswers));
                  
                  saveSession(allAnswers).then((sessionId) => {
                    if (sessionId) {
                      message.success('恭喜你完成了情绪管理五步法！');
                      // 立即刷新历史会话
                      fetchHistorySessions();
                      // 跳转到历史记录页面
                      setActiveTab('history');
                    } else {
                      message.error('保存失败，请重试');
                    }
                  }).catch((error) => {
                    console.error('保存会话时发生错误:', error);
                    message.error('保存失败，请重试');
                  });
                }}
                disabled={(() => {
                  const currentKey = `${currentStep}-${currentQuestion}`;
                  const currentAnswer = answers[currentKey];
                  const hasAnswer = currentAnswer && (
                    Array.isArray(currentAnswer) ? currentAnswer.length > 0 : 
                    typeof currentAnswer === 'string' ? currentAnswer.trim() !== '' : 
                    currentAnswer !== ''
                  );
                  console.log('提交按钮状态检查:', { currentAnswer, hasAnswer });
                  return !hasAnswer;
                })()}
                size="large"
                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              >
                提交完成
              </Button>
            ) : (
              // 其他步骤显示下一步按钮
              <Button 
                type="primary"
                icon={<ArrowRightOutlined />}
                onClick={nextQuestion}
                disabled={!canProceed()}
                size="large"
                style={{ backgroundColor: '#FF6B6B', borderColor: '#FF6B6B' }}
              >
                下一步
              </Button>
            );
            })()}
          </div>
        </Card>
      </div>
    );
  };

  const renderSummary = () => {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <CheckCircleOutlined style={{ fontSize: '64px', color: '#52c41a', marginBottom: '20px' }} />
        <Title level={2} style={{ color: '#52c41a', marginBottom: '20px' }}>
          情绪管理完成！
        </Title>
        <Paragraph style={{ fontSize: '16px', color: '#666', marginBottom: '30px' }}>
          你已经完成了情绪管理五步法，这是一个很好的开始。
          <br />
          记住：看见 → 命名 → 调节 → 表达 → 转化，这是一个完整的"消化情绪"流程。
        </Paragraph>
        
        <div style={{ marginBottom: '30px' }}>
          <Title level={4}>你的情绪管理记录：</Title>
          {Object.entries(answers).map(([key, value]) => {
            const [step, question] = key.split('-');
            const stepName = steps[parseInt(step)].title;
            const questionText = questions[parseInt(step)][parseInt(question)].question;
            const questionData = questions[parseInt(step)][parseInt(question)];
            
            // 处理多选答案的显示
            const displayValue = Array.isArray(value) ? value.join('、') : value;
            
            return (
              <div key={key} style={{ 
                textAlign: 'left', 
                margin: '10px 0', 
                padding: '10px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '8px' 
              }}>
                <Text strong>{stepName}: </Text>
                <Text>{questionText}</Text>
                {questionData.multiple && (
                  <Text style={{ fontSize: '12px', color: '#999', marginLeft: '8px' }}>
                    (多选)
                  </Text>
                )}
                <br />
                <Text style={{ color: '#FF6B6B' }}>你的回答: {displayValue}</Text>
              </div>
            );
          })}
        </div>

        <Button 
          type="primary" 
          size="large"
          onClick={() => {
            setCurrentStep(0);
            setCurrentQuestion(0);
            setAnswers({});
            setActiveTab('guide');
          }}
          style={{ backgroundColor: '#FF6B6B', borderColor: '#FF6B6B', marginRight: '10px' }}
        >
          重新开始
        </Button>
        <Button 
          size="large"
          onClick={() => navigate('/')}
        >
          返回首页
        </Button>
      </div>
    );
  };

  // 渲染历史会话
  // 树洞：一句话心情
  const renderTreeHole = () => {
    return (
      <div style={{ padding: '12px 6px 24px' }}>
        {/* 记录区 */}
        <Card size="small" style={{ borderRadius: 14, marginBottom: 14 }} bodyStyle={{ padding: 14 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
            {MOODS.map((m) => (
              <span
                key={m.key}
                onClick={() => setTreeMood(m.key)}
                style={{
                  cursor: 'pointer', userSelect: 'none',
                  padding: '4px 10px', borderRadius: 16, fontSize: 13,
                  border: `1px solid ${treeMood === m.key ? m.color : '#eee'}`,
                  background: treeMood === m.key ? m.color : '#fafafa',
                  color: treeMood === m.key ? '#fff' : '#555',
                }}
              >
                {m.emoji} {m.key}
              </span>
            ))}
          </div>
          <Input.TextArea
            value={treeContent}
            onChange={(e) => setTreeContent(e.target.value)}
            placeholder="记一句此刻的心情…"
            autoSize={{ minRows: 2, maxRows: 4 }}
            maxLength={500}
            style={{ borderRadius: 10, marginBottom: 10 }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="primary" icon={<CloudOutlined />} loading={treeSaving} onClick={addTreeHole} style={{ borderRadius: 10 }}>
              记录心情
            </Button>
          </div>
        </Card>

        {/* 心情列表（时间轴） */}
        {treeList.length === 0 ? (
          <Empty description={treeLoading ? '加载中…' : '还没有记录，写下第一句心情吧'} style={{ marginTop: 40 }} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <List
            dataSource={treeList}
            renderItem={(item) => {
              const m = MOOD_MAP[item.mood] || { emoji: '🍃', color: '#bbb', key: item.mood || '' };
              return (
                <List.Item style={{ padding: '10px 4px' }}>
                  <div style={{ display: 'flex', gap: 10, width: '100%' }}>
                    <div style={{ fontSize: 22, lineHeight: '24px' }}>{m.emoji}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                        {item.mood && <Tag color={m.color} style={{ marginInlineEnd: 0, borderRadius: 8 }}>{item.mood}</Tag>}
                        <span style={{ fontSize: 12, color: '#bbb' }}>{dayjs(item.created_at).format('YYYY-MM-DD HH:mm')}</span>
                      </div>
                      <div style={{ color: '#333', fontSize: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{item.content}</div>
                    </div>
                    <Popconfirm title="删除这条心情？" onConfirm={() => deleteTreeHole(item.id)} okText="删除" cancelText="取消">
                      <DeleteOutlined style={{ color: '#ccc', cursor: 'pointer', paddingTop: 2 }} />
                    </Popconfirm>
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </div>
    );
  };

  const renderHistory = () => {
    if (selectedSession) {
      // 显示会话详情
      return (
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <Title level={3} style={{ margin: 0, color: '#FF6B6B' }}>
              <HistoryOutlined style={{ marginRight: '8px' }} />
              会话详情
            </Title>
            <Button 
              onClick={() => {
                setSelectedSession(null);
                setSessionDetails([]);
              }}
              size="small"
            >
              返回列表
            </Button>
          </div>
          
          <div>
            {sessionDetails.map((detail, index) => (
              <Card 
                key={detail.id} 
                style={{ marginBottom: '16px' }}
                size="small"
              >
                <div style={{ marginBottom: '8px' }}>
                  <Tag color="#FF6B6B" style={{ marginRight: '8px' }}>
                    {detail.step_name}
                  </Tag>
                  <Text style={{ fontSize: '12px', color: '#999' }}>
                    步骤 {detail.step_order}
                  </Text>
                </div>
                <div style={{ marginBottom: '4px' }}>
                  <Text strong>问题：</Text>
                  <Text>{detail.question}</Text>
                </div>
                <div>
                  <Text strong style={{ color: '#FF6B6B' }}>回答：</Text>
                  <Text style={{ color: '#FF6B6B' }}>{detail.answer}</Text>
                </div>
              </Card>
            ))}
          </div>
        </div>
      );
    }

    // 显示会话列表
    return (
      <div style={{ padding: '20px 6px' }}>
        {historySessions.length > 0 && (
          <div className="mod-stats" style={{ marginBottom: '20px' }}>
            <div className="mod-stat-card">
              <div className="mod-stat-icon"><HistoryOutlined /></div>
              <div className="mod-stat-value">{historySessions.length}</div>
              <div className="mod-stat-label">累计梳理次数</div>
            </div>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div className="mod-section-title" style={{ margin: 0 }}>
            <HistoryOutlined style={{ marginRight: '8px', color: '#6366F1' }} />
            情绪管理历史记录
          </div>
          <Button
            onClick={fetchHistorySessions}
            loading={loading}
            size="small"
          >
            刷新
          </Button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text>加载中...</Text>
          </div>
        ) : historySessions.length === 0 ? (
          <Empty
            style={{ padding: '40px' }}
            description="暂无历史记录，完成一次情绪管理后即可查看"
          />
        ) : (
          <div>
            {historySessions.map((session) => (
              <Card
                key={session.id}
                className="mod-card"
                style={{ marginBottom: '16px', cursor: 'pointer' }}
                bodyStyle={{ padding: '16px 18px' }}
                onClick={() => fetchSessionDetails(session.id)}
                hoverable
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div
                      style={{
                        width: '40px', height: '40px', borderRadius: '12px',
                        background: 'rgba(99,102,241,0.12)', color: '#6366F1',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        marginRight: '12px', flexShrink: 0
                      }}
                    >
                      <HeartOutlined />
                    </div>
                    <div>
                      <div style={{ marginBottom: '4px' }}>
                        <Text strong>情绪管理会话</Text>
                      </div>
                      <div style={{ marginBottom: '4px' }}>
                        <Text style={{ fontSize: '12px', color: '#666' }}>
                          {session.emotion_summary || '情绪管理五步法'}
                        </Text>
                      </div>
                      <div>
                        <Text style={{ fontSize: '12px', color: '#999' }}>
                          {new Date(session.session_date).toLocaleString('zh-CN')}
                        </Text>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Text style={{ fontSize: '12px', color: '#6366F1' }}>
                      点击查看详情 →
                    </Text>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mod-page">
      {/* 头部导航 */}
      <div className="mod-header">
        <div>
          <div className="mod-header-title">
            <HeartOutlined style={{ marginRight: '10px', color: '#6366F1' }} />
            情绪管理五步法
          </div>
          <div className="mod-header-subtitle">
            觉察 · 命名 · 调节 · 表达 · 转化，一步步消化并转化你的情绪
          </div>
        </div>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/')}
          size="large"
        >
          返回首页
        </Button>
      </div>

      {/* 标签页 */}
      <Tabs
        activeKey={activeTab} 
        onChange={(key) => {
          setActiveTab(key);
          if (key === 'history') {
            // 重置选中状态并刷新数据
            setSelectedSession(null);
            setSessionDetails([]);
            fetchHistorySessions();
          }
          if (key === 'treehole') {
            fetchTreeHole();
          }
        }}
        style={{ padding: '0 6px' }}
        items={[
          {
            key: 'guide',
            label: '情绪管理',
            children: (
              <div>
                {/* 进度条 + 步骤指示器 */}
                <Card className="mod-toolbar" style={{ marginBottom: '24px' }}>
                  <div style={{ marginBottom: '20px' }}>
                    <Progress
                      percent={getProgress()}
                      strokeColor="#6366F1"
                      format={(percent) => `${percent}% 完成`}
                    />
                  </div>

                  <Steps current={currentStep} size="small">
                    {steps.map((step, index) => (
                      <Step
                        key={index}
                        title={step.title}
                        description={step.subtitle}
                      />
                    ))}
                  </Steps>
                </Card>

                {/* 主要内容 */}
                {(() => {
                  const progress = getProgress();
                  const isLastStep = currentStep === steps.length - 1;
                  const isLastQuestion = currentQuestion === questions[currentStep].length - 1;
                  const isLastStepCompleted = isLastStep && isLastQuestion && progress === 100;
                  
                  console.log('渲染检查:', {
                    progress,
                    isLastStep,
                    isLastQuestion,
                    isLastStepCompleted
                  });
                  
                  // 只有在最后一步完成且用户点击了提交按钮后才显示总结
                  if (isLastStepCompleted && activeTab === 'history') {
                    return renderSummary();
                  } else {
                    return renderStepContent();
                  }
                })()}
              </div>
            )
          },
          {
            key: 'history',
            label: (
              <span>
                <HistoryOutlined style={{ marginRight: '4px' }} />
                历史记录
              </span>
            ),
            children: renderHistory()
          },
          {
            key: 'treehole',
            label: (
              <span>
                <CloudOutlined style={{ marginRight: '4px' }} />
                树洞
              </span>
            ),
            children: renderTreeHole()
          }
        ]}
      />
    </div>
  );
};

export default Emotions;

